import React from 'react';
import { Box, Heading, Text, Flex, HStack, IconButton, Image, Spacer } from '@chakra-ui/react';
import { FiTrash2, FiMapPin, FiGlobe, FiShield, FiClock, FiCheckCircle, FiEdit2, FiShare2, FiArrowRight, FiX } from 'react-icons/fi';
import { formatLabel, isOnlineFormat } from './eventConstants';
import { parseCoverImage } from '../../services/api';

const FORMAT_STYLES = {
  in_person: { color: 'rgba(122, 226, 161, 0.95)', bg: 'rgba(122, 226, 161, 0.12)', border: 'rgba(122, 226, 161, 0.22)' },
  online:    { color: 'rgba(147, 197, 253, 0.95)', bg: 'rgba(147, 197, 253, 0.12)', border: 'rgba(147, 197, 253, 0.22)' },
  hybrid:    { color: 'rgba(216, 180, 254, 0.95)', bg: 'rgba(216, 180, 254, 0.12)', border: 'rgba(216, 180, 254, 0.22)' },
};

const COMPLIANCE_CONFIG = {
  pending: {
    icon: FiClock,
    label: 'Pending compliance review',
    color: 'rgba(251,191,36,0.9)',
    bg: 'rgba(251,191,36,0.08)',
    border: 'rgba(251,191,36,0.2)',
  },
  approved: {
    icon: FiCheckCircle,
    label: 'Compliance approved',
    color: 'rgba(122,226,161,0.9)',
    bg: 'rgba(122,226,161,0.08)',
    border: 'rgba(122,226,161,0.2)',
  },
  changes_requested: {
    icon: FiEdit2,
    label: 'Changes requested',
    color: 'rgba(251,191,36,0.9)',
    bg: 'rgba(251,191,36,0.08)',
    border: 'rgba(251,191,36,0.2)',
  },
  rejected: {
    icon: FiX,
    label: 'Rejected by compliance',
    color: 'rgba(248,113,113,0.9)',
    bg: 'rgba(248,113,113,0.08)',
    border: 'rgba(248,113,113,0.2)',
  },
  draft: {
    icon: FiShield,
    label: 'Draft — not submitted',
    color: 'rgba(255,255,255,0.4)',
    bg: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.10)',
  },
};

const Pill = ({ children, styles }) => (
  <Box
    px={2}
    py="2px"
    borderRadius="full"
    bg={styles?.bg || 'rgba(255,255,255,0.06)'}
    border={`1px solid ${styles?.border || 'rgba(255,255,255,0.12)'}`}
  >
    <Text
      fontSize="10px"
      fontWeight="600"
      color={styles?.color || 'rgba(255,255,255,0.55)'}
      letterSpacing="0.06em"
      textTransform="uppercase"
    >
      {children}
    </Text>
  </Box>
);

const EventCard = ({ event, onDelete, onEdit, onShare, onView }) => {
  const title = event?.title || 'Untitled Event';
  const online = isOnlineFormat(event?.format);
  const location =
    event?.format === 'online'
      ? 'Online event'
      : event?.venueName || event?.city || 'Venue TBD';

  const city = event?.city || null;

  // Derive compliance display state from compliance_status (the authoritative
  // gate). No compliance status at all → it's still a draft.
  const complianceKey =
    event?.complianceStatus === 'approved' ? 'approved' :
    event?.complianceStatus === 'rejected' ? 'rejected' :
    event?.complianceStatus === 'changes_requested' ? 'changes_requested' :
    event?.complianceStatus === 'pending' ? 'pending' :
    'draft';
  const compliance = COMPLIANCE_CONFIG[complianceKey];
  const ComplianceIcon = compliance.icon;
  const isApproved = complianceKey === 'approved';
  const shareable = isApproved && Boolean(onShare);
  const viewable = isApproved && Boolean(onView);
  const editable = Boolean(onEdit);

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(event.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(event.id);
  };

  const handleShare = (e) => {
    e?.stopPropagation();
    onShare?.(event);
  };

  const handleView = (e) => {
    e?.stopPropagation();
    onView?.(event);
  };

  // The whole card is the primary action: edit while pending/draft, open the
  // read-only details once approved (so the advisor can publish externally).
  // The "Share event" CTA is a discrete control on top of that. Delete also
  // stops propagation.
  const handleCardClick = editable ? handleEdit : viewable ? handleView : shareable ? handleShare : undefined;

  return (
    <Box
      bg="#0f0f0f"
      borderRadius="12px"
      border="1px solid rgba(255,255,255,0.10)"
      overflow="hidden"
      transition="all 0.2s"
      role="group"
      cursor={handleCardClick ? 'pointer' : 'default'}
      onClick={handleCardClick}
      _hover={{ transform: 'translateY(-2px)', borderColor: 'rgba(255,255,255,0.22)' }}
    >
      {/* Cover band — 16:9 for a sleek, horizontal grid card. The focal point
          (object-position) keeps the subject framed, so it reads like the 2:1
          attendee preview without making the card tall/heavy. Source-of-truth
          ratio stays 2:1 on the uploader + attendee preview + focal modal. */}
      <Box position="relative" w="100%" overflow="hidden" sx={{ aspectRatio: '16 / 9' }}>
        {event?.coverImage ? (
          <Image src={parseCoverImage(event.coverImage).src} alt={title} w="100%" h="100%" objectFit="cover" objectPosition={parseCoverImage(event.coverImage).objectPosition} />
        ) : (
          <Box
            w="100%"
            h="100%"
            bgGradient="linear(135deg, #1f5e3a 0%, #2f8f57 60%, rgba(99,102,241,0.3) 100%)"
          />
        )}
        <Box position="absolute" top={3} left={3}>
          <Pill styles={FORMAT_STYLES[event?.format]}>{formatLabel(event?.format)}</Pill>
        </Box>
        {event?.category && (
          <Box position="absolute" bottom={3} left={3}>
            <Pill>{event.category}</Pill>
          </Box>
        )}
        {onDelete && (
          <IconButton
            icon={<FiTrash2 size={14} />}
            aria-label="Delete event"
            size="sm"
            position="absolute"
            top={2}
            right={2}
            minW="30px"
            h="30px"
            borderRadius="full"
            bg="rgba(0,0,0,0.45)"
            backdropFilter="blur(6px)"
            border="1px solid rgba(255,255,255,0.15)"
            color="#fafafa"
            opacity={0}
            pointerEvents="none"
            _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
            _hover={{ color: 'rgba(239,68,68,0.95)', bg: 'rgba(0,0,0,0.6)', borderColor: 'rgba(239,68,68,0.5)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={handleDelete}
          />
        )}
      </Box>

      {/* Body */}
      <Box p={4}>
        {/* Title spans the full width so it packs as much as possible on line 1
            before wrapping to a second line. */}
        <Heading size="sm" noOfLines={2} color="#fafafa" fontWeight="600" letterSpacing="-0.01em" lineHeight="1.4" mb={2}>
          {title}
        </Heading>

        {/* Location */}
        <HStack spacing={2} mb={3} color="#a3a3a3" fontSize="xs">
          {online && event?.format === 'online' ? <FiGlobe size={12} /> : <FiMapPin size={12} />}
          <Text noOfLines={1}>
            {location}
            {city && event?.venueName && city !== location ? ` · ${city.split(',')[0]}` : ''}
          </Text>
        </HStack>

        {/* Status strip — approved cards turn into a Share CTA */}
        {shareable ? (
          <Flex
            align="center"
            gap={2}
            px={3}
            py={2}
            bg="rgba(122,226,161,0.1)"
            border="1px solid rgba(122,226,161,0.25)"
            borderRadius="8px"
            transition="all 0.15s"
            cursor="pointer"
            role="button"
            aria-label="Share event"
            onClick={handleShare}
            _hover={{ bg: 'rgba(122,226,161,0.16)', borderColor: 'rgba(122,226,161,0.4)' }}
          >
            <FiShare2 size={12} color="rgba(122,226,161,0.95)" style={{ flexShrink: 0 }} />
            <Text fontSize="xs" fontWeight="600" color="rgba(122,226,161,0.95)" noOfLines={1}>
              Share event
            </Text>
            <Spacer />
            <FiArrowRight size={13} color="rgba(122,226,161,0.7)" style={{ flexShrink: 0 }} />
          </Flex>
        ) : (
          <Flex
            align="center"
            gap={2}
            px={3}
            py={2}
            bg={compliance.bg}
            border={`1px solid ${compliance.border}`}
            borderRadius="8px"
          >
            <ComplianceIcon size={12} color={compliance.color} style={{ flexShrink: 0 }} />
            <Text fontSize="xs" fontWeight="500" color={compliance.color} noOfLines={1}>
              {compliance.label}
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default EventCard;
